<template>
  <div class="main-popups">
    <portal v-if="visiblePopupKey === 'notificationIntroductionPopup'" to="confirm-modal">
      <notification-introduction-popup @close="onPopupClose('notificationIntroductionPopup')" />
    </portal>
  </div>
</template>

<script>
import NotificationIntroductionPopup from '@/components/popups/main-popups/NotificationIntroductionPopup.vue'

export default {
  name: 'MainPopups',
  components: { NotificationIntroductionPopup },
  data() {
    return {
      /** Currently visible popup key, or null */
      visiblePopupKey: null,
      /** Queue of popups waiting to be shown */
      popupQueue: [],
      /** Server-side popup states (fetched once) */
      states: [],
      statesLoaded: false,
      /** Popups requested before states were loaded */
      pendingToShow: {}
    }
  },
  created() {
    console.log('[MainPopups] created — subscribing to $popup')
    this.unsubscribe = this.$popup.subscribe(this.requestPopup)
    this.fetchPopupStates()
  },
  beforeDestroy() {
    this.unsubscribe && this.unsubscribe()
  },
  methods: {
    // ── Server-side popup states ──────────────────────────────

    async fetchPopupStates() {
      try {
        const { states } = await this.$api.popupStates.list()
        this.states = Array.isArray(states) ? states : []
      } finally {
        this.statesLoaded = true
        this.drainPending()
      }
    },

    // ── Unified popup request ──────────────────────────────────

    /**
     * Called from $popup.request(). Decides whether popup should show,
     * queues it if yes, and starts processing the queue.
     */
    requestPopup({ popupKey }) {
      console.log(`[MainPopups] requestPopup called: "${popupKey}"`)

      // Пока states с сервера не загружены — откладываем ВСЕ попапы,
      // чтобы не сломать порядок (server-зависимые уходят в pending,
      // а локальные проскакивают первыми)
      if (!this.statesLoaded) {
        console.log(`[MainPopups] → deferred (states not loaded yet)`)
        this.$set(this.pendingToShow, popupKey, true)
        return
      }

      const shouldShow = this.shouldShowPopup(popupKey)
      console.log(`[MainPopups] shouldShowPopup("${popupKey}") = ${shouldShow}`)
      if (shouldShow) {
        this.popupQueue.push(popupKey)
        console.log(`[MainPopups] queue now: [${this.popupQueue.join(', ')}]`)
        this.processQueue()
      }
    },

    shouldShowPopup(popupKey) {
      switch (popupKey) {
        case 'notificationIntroductionPopup':
          return this.shouldShowServerPopup(popupKey)
        default:
          return false
      }
    },

    shouldShowServerPopup(popupKey) {
      return !this.states.find((s) => s.popupKey === popupKey)?.isViewed
    },

    // ── Queue management ───────────────────────────────────────

    /** Show the next popup in queue, if none is currently visible */
    processQueue() {
      console.log(
        `[MainPopups] processQueue: visible="${this.visiblePopupKey}", queue=[${this.popupQueue.join(', ')}]`
      )
      if (this.visiblePopupKey || !this.popupQueue.length) {
        console.log(
          `[MainPopups] processQueue: nothing to show (blocked by ${this.visiblePopupKey ? 'visible popup' : 'empty queue'})`
        )
        return
      }
      this.visiblePopupKey = this.popupQueue.shift()
      console.log(`[MainPopups] → now showing: "${this.visiblePopupKey}"`)
    },

    /** Called when a popup emits @close */
    onPopupClose(popupKey) {
      this.savePopupState(popupKey)
      this.visiblePopupKey = null
      this.processQueue()
    },

    /**
     * Save popup state after it was shown/closed.
     * Server popups → API call; local popups → localStorage.
     */
    savePopupState(popupKey) {
      switch (popupKey) {
        case 'notificationIntroductionPopup': {
          this.$api.popupStates.create({ popupKey })
          const idx = this.states.findIndex((s) => s.popupKey === popupKey)
          if (idx === -1) {
            this.states.push({ popupKey, isViewed: true })
          } else {
            this.$set(this.states, idx, { ...this.states[idx], isViewed: true })
          }
          break
        }
      }
    },

    // ── Pending (deferred) popups ──────────────────────────────

    /** After states are loaded, re-evaluate deferred popups */
    drainPending() {
      const keys = Object.keys(this.pendingToShow)
      if (!keys.length) return
      this.pendingToShow = {}
      keys.forEach((popupKey) => this.requestPopup({ popupKey }))
    }
  }
}
</script>

<style scoped lang="scss">
.MainPopups {
}
</style>
